import { isEmpty } from "lodash";
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useSchool } from "@app/shared/lib/hooks";
import { useUser } from "@app/shared/lib/hooks/useUser";

type ProfileTab = "common" | "notifications" | "bonus" | "qrcode";

/** Profile popup style overrides injected by CabinetMenuWidget so the
 *  global StudentProfileModal applies the configured colors/font. */
export type CabinetProfileSettings = {
  bgColor?: string;
  fontFamily?: string;
  nameColor?: string;       // headings color
  nameFontSize?: string;    // headings size
  emailColor?: string;      // body text color
  emailFontSize?: string;   // body text size
  btnBgColor?: string;
  btnTextColor?: string;
  btnBorderRadius?: string;
};

export type UIAppStateContextType = {
  isEmailConfirmationModalVisible: boolean;
  isEmailConfirmationMessageVisible: boolean;
  isSchoolTransferred: boolean;
  isProfileDrawerVisible: boolean;
  isProfileModalVisible: boolean;
  defaultProfileTab: ProfileTab;
  needChangePassword: boolean;
  cabinetProfileSettings: CabinetProfileSettings | null;
  setCabinetProfileSettings: (s: CabinetProfileSettings | null) => void;
  setNeedChangePassword: (needChangePassword: boolean) => void;
  setIsProfileDrawerVisible: (
    IsProfileDrawerVisible: boolean,
    profileTab?: ProfileTab,
  ) => void;
  setIsEmailConfirmationModalVisible: (
    isEmailConfirmationModalVisible: boolean,
  ) => void;
};

const UIAppStateContext = createContext({} as UIAppStateContextType);

export const UIAppStateProvider: FC<PropsWithChildren> = ({ children }) => {
  const school = useSchool();
  const { user } = useUser();
  const [isEmailConfirmationModalVisible, setIsEmailConfirmationModalVisible] =
    useState(false);
  const [isProfileDrawerVisible, setIsProfileDrawerVisibleRaw] =
    useState(false);
  const [defaultProfileTab, setDefaultProfileTab] =
    useState<ProfileTab>("common");
  const [needChangePassword, setNeedChangePassword] = useState(
    !!user?.needChangePassword,
  );
  const [
    isEmailConfirmationMessageVisible,
    setIsEmailConfirmationMessageVisible,
  ] = useState(false);
  const [cabinetProfileSettings, setCabinetProfileSettings] =
    useState<CabinetProfileSettings | null>(null);

  useEffect(() => {
    setNeedChangePassword(!!user?.needChangePassword);
  }, [user?.needChangePassword]);
  useEffect(() => {
    const settings = school.profileSettings;
    const needConfirmEmail =
      settings.emailNeedConfirm && user?.isSchoolEmailConfirmed === false;
    setIsEmailConfirmationModalVisible(
      needConfirmEmail && settings.blockAccessIfEmailUnconfirmed,
    );
  }, [school.profileSettings, user?.isSchoolEmailConfirmed]);

  useEffect(() => {
    const settings = school.profileSettings;
    const needConfirmEmail =
      settings.emailNeedConfirm && user?.isSchoolEmailConfirmed === false;
    setIsEmailConfirmationMessageVisible(
      needConfirmEmail && !settings.blockAccessIfEmailUnconfirmed,
    );
  }, [school.profileSettings, user?.isSchoolEmailConfirmed]);

  const isProfileModalVisible = useMemo(() => {
    const curUser = user;
    const settings = school.profileSettings;
    if (!curUser) return false;

    return (
      curUser.email == null ||
      (settings.firstNameRequired === true && isEmpty(curUser.firstName)) ||
      (settings.lastNameRequired === true && isEmpty(curUser.lastName)) ||
      (settings.middleNameRequired === true && isEmpty(curUser.middleName)) ||
      (settings.genderRequired === true && curUser.gender == null) ||
      (settings.phoneRequired === true && isEmpty(curUser.phone)) ||
      (settings.birthdayRequired === true && curUser.birthday == null)
    );
  }, [school.profileSettings, user]);

  const isSchoolTransferred = useMemo(() => {
    if (user == null) return false;

    return !!(
      school.migration &&
      (school.migration.isActive || school.migration.isCompleted)
    );
  }, [school.migration, user]);

  const setIsProfileDrawerVisible = useCallback(
    (val: boolean, defaultTab?: ProfileTab) => {
      setIsProfileDrawerVisibleRaw(val);
      setDefaultProfileTab(defaultTab ?? "common");
    },
    [],
  );

  return (
    <UIAppStateContext.Provider
      value={useMemo(
        () => ({
          isSchoolTransferred,
          isProfileModalVisible,
          defaultProfileTab,
          isEmailConfirmationMessageVisible,
          isProfileDrawerVisible,
          setIsProfileDrawerVisible,
          isEmailConfirmationModalVisible,
          setIsEmailConfirmationModalVisible,
          needChangePassword,
          setNeedChangePassword,
          cabinetProfileSettings,
          setCabinetProfileSettings,
        }),
        [
          isSchoolTransferred,
          defaultProfileTab,
          isProfileModalVisible,
          isEmailConfirmationMessageVisible,
          isEmailConfirmationModalVisible,
          isProfileDrawerVisible,
          needChangePassword,
          setIsProfileDrawerVisible,
          cabinetProfileSettings,
        ],
      )}
    >
      {children}
    </UIAppStateContext.Provider>
  );
};

export const useUIAppState = () => useContext(UIAppStateContext);
